import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, ShieldAlert, RefreshCw, Search } from 'lucide-react';

const eventLabels = {
  login_failed: { label: 'Login Failed', className: 'bg-red-50 text-red-700 border-red-200' },
  otp_failed: { label: 'OTP Failed', className: 'bg-amber-50 text-amber-700 border-amber-200' },
};

const reasonLabels = {
  invalid_credentials: 'Invalid credentials',
  captcha_failed: 'CAPTCHA failed',
  account_pending: 'Account pending approval',
  account_rejected: 'Account rejected',
  account_suspended: 'Account suspended',
};

export default function LoginAuditPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eventFilter, setEventFilter] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');

  const loadLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (eventFilter !== 'all') params.event = eventFilter;
      if (emailSearch.trim()) params.email = emailSearch.trim();
      const res = await authAPI.loginAudit(params);
      setLogs(res.data);
    } catch (err) {
      toast.error('Failed to load login audit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); /* eslint-disable-next-line */ }, [eventFilter]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/users')} className="text-slate-600" data-testid="back-to-users-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> User Management
          </Button>
          <div className="h-4 w-px bg-slate-300" />
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-600" />
            <h1 className="text-lg font-semibold text-[#0F172A]">Login Audit</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6" data-testid="login-audit-page">
        <div className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]">Login Security Audit</h1>
              <p className="text-sm text-slate-500 mt-0.5">Failed login and OTP attempts — time, email and IP</p>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex items-center gap-1">
                <Input
                  value={emailSearch}
                  onChange={(e) => setEmailSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadLogs()}
                  placeholder="Search email"
                  className="h-9 w-48 text-sm"
                  data-testid="login-audit-email-search"
                />
                <Button size="sm" variant="outline" onClick={loadLogs} className="h-9" data-testid="login-audit-search-btn">
                  <Search className="w-3.5 h-3.5" />
                </Button>
              </div>
              <Select value={eventFilter} onValueChange={setEventFilter}>
                <SelectTrigger className="w-40 h-9" data-testid="login-audit-event-filter">
                  <SelectValue placeholder="Filter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="login_failed">Login Failed</SelectItem>
                  <SelectItem value="otp_failed">OTP Failed</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={loadLogs} className="h-9 gap-1.5" data-testid="login-audit-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs font-semibold text-slate-500 w-[180px]">Timestamp (UTC)</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 w-[120px]">Event</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500">Email</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 w-[150px]">IP Address</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 w-[180px]">Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-slate-400">Loading...</TableCell></TableRow>
                ) : logs.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-sm text-slate-400">No failed attempts recorded</TableCell></TableRow>
                ) : logs.map((log, i) => {
                  const ev = eventLabels[log.event] || { label: log.event, className: 'bg-slate-50 text-slate-600' };
                  return (
                    <TableRow key={log.id || i} data-testid={`login-audit-row-${i}`} className="hover:bg-slate-50/50">
                      <TableCell className="font-mono text-[11px] text-slate-500 whitespace-nowrap">
                        {log.timestamp?.replace('T', ' ').substring(0, 19)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] ${ev.className}`}>{ev.label}</Badge>
                      </TableCell>
                      <TableCell className="text-xs font-medium text-[#0F172A]">{log.email || '—'}</TableCell>
                      <TableCell className="text-xs font-mono text-slate-500">{log.ip || '—'}</TableCell>
                      <TableCell className="text-xs text-slate-500">{reasonLabels[log.reason] || log.reason || '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  );
}
